import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Avatar,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  Fade,
  Chip,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  FitnessCenter as FitnessCenterIcon,
  CreditCard as CreditCardIcon,
  Badge as BadgeIcon,
  QrCode2 as QrCodeIcon,
} from "@mui/icons-material";
import axios from "axios";
import { Html5Qrcode } from "html5-qrcode";
import config from "../../config";
import { resolveProfileImageUrl } from "../../utils/profileImageStorage";
import { getAuthToken } from "../../utils/authStorage";

// Kiosk theme colors (matching original desktop app)
const kioskColors = {
  background: "#1a1a2e",
  headerBg: "#16213e",
  cardBg: "#0f3460",
  accent: "#00d4ff",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  textPrimary: "#ffffff",
  textSecondary: "#95a5a6",
  textMuted: "#7f8c8d",
};

// Capture GPS only for QR attendance; RFID and manual Member ID stay unchanged.
const getQrLocation = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error("Location is not supported by this browser."));
    return;
  }

  navigator.geolocation.getCurrentPosition(
    ({ coords }) => resolve({
      latitude: coords.latitude,
      longitude: coords.longitude,
      accuracy: coords.accuracy,
    }),
    (error) => {
      const messages = {
        1: "Location permission was denied. Allow location access and try again.",
        2: "Your current location is unavailable. Try again near the branch.",
        3: "Location request timed out. Try again.",
      };
      reject(new Error(messages[error.code] || "Could not determine your location."));
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );
});

const AttendanceKiosk = () => {
  // State management
  const [step, setStep] = useState("discipline"); // 'discipline' | 'attendance'
  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [selectedDisciplineName, setSelectedDisciplineName] = useState("");
  const [rfidInput, setRfidInput] = useState("");
  const [memberIdInput, setMemberIdInput] = useState("");
  const [inputMode, setInputMode] = useState("rfid"); // 'rfid' | 'memberId' | 'qr'
  const [qrAction, setQrAction] = useState("check_in"); // 'check_in' | 'check_out'
  const [qrScannerError, setQrScannerError] = useState("");
  const [qrScannerKey, setQrScannerKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success' | 'warning' | 'error', message, member }
  const [disciplineError, setDisciplineError] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");

  const rfidInputRef = useRef(null);
  const lastKeyTime = useRef(0);
  const scanBuffer = useRef("");
  const qrScannerRef = useRef(null);
  const qrScannerStartingRef = useRef(false);
  const qrActionRef = useRef(qrAction);

  // Fetch disciplines on mount
  useEffect(() => {
    fetchDisciplines();
  }, []);

  // Auto-focus the legacy keyboard input only; QR mode owns the camera instead.
  useEffect(() => {
    if (step === "attendance" && rfidInputRef.current) {
      rfidInputRef.current.focus();
    }
  }, [step, result]);

  // Stop the camera before changing modes or leaving the kiosk screen.
  const stopQrScanner = useCallback(async () => {
    const scanner = qrScannerRef.current;
    qrScannerRef.current = null;
    qrScannerStartingRef.current = false;

    if (!scanner) return;

    try {
      if (scanner.isScanning) {
        await scanner.stop();
      }
      scanner.clear();
    } catch (error) {
      console.warn("Failed to stop QR scanner:", error);
    }
  }, []);

  useEffect(() => {
    if (step !== "attendance" || inputMode !== "qr") {
      stopQrScanner();
      return undefined;
    }

    let cancelled = false;

    const startQrScanner = async () => {
      if (qrScannerRef.current || qrScannerStartingRef.current) return;

      qrScannerStartingRef.current = true;
      setQrScannerError("");
      const scanner = new Html5Qrcode("attendance-qr-reader");
      qrScannerRef.current = scanner;

      try {
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            if (cancelled || loading || result) return;

            const membershipId = decodedText.trim();
            if (!membershipId || /[\r\n]/.test(membershipId)) {
              setQrScannerError("Invalid QR code. Please scan a TEBA member QR code.");
              return;
            }

            await stopQrScanner();
            setMemberIdInput(membershipId);
            setQrScannerError("");
            // Read the latest action so a scanner started in Check-In mode cannot submit Check-Out scans as Check-In.
            handleSubmitAttendance("qr", membershipId, qrActionRef.current);
          },
          () => {
            // Frame-level decode misses are expected while the camera is running.
          }
        );
      } catch (error) {
        if (!cancelled) {
          setQrScannerError(
            "Camera access failed. Allow camera permission and try QR mode again."
          );
        }
        await stopQrScanner();
      } finally {
        qrScannerStartingRef.current = false;
      }
    };

    startQrScanner();

    return () => {
      cancelled = true;
      stopQrScanner();
    };
  }, [inputMode, step, qrScannerKey, stopQrScanner]);

  // Keep focus on input field - RFID scanners need the input to be focused.
  // This intentionally excludes QR mode so camera controls are not interrupted.
  useEffect(() => {
    if (step !== "attendance" || loading) return;

    const handleGlobalClick = (e) => {
      // If clicking outside the mode toggle buttons, refocus the input
      const isButton = e.target.closest('button');
      if (!isButton && rfidInputRef.current && inputMode === "rfid") {
        rfidInputRef.current.focus();
      }
    };

    // Refocus input when it loses focus (unless loading or clicking buttons)
    const handleFocusOut = () => {
      if (!loading && rfidInputRef.current && inputMode === "rfid") {
        setTimeout(() => {
          if (document.activeElement?.tagName !== 'BUTTON' && rfidInputRef.current) {
            rfidInputRef.current.focus();
          }
        }, 100);
      }
    };

    document.addEventListener('click', handleGlobalClick);
    rfidInputRef.current?.addEventListener('blur', handleFocusOut);

    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [step, loading, inputMode]);

  // Handle RFID scanner input - scanners type very fast and end with Enter
  const handleRfidChange = useCallback((e) => {
    const currentTime = Date.now();
    const timeDiff = currentTime - lastKeyTime.current;
    const newValue = e.target.value;

    // If there's a result showing and user starts typing, clear everything first
    if (result) {
      setResult(null);
      setRfidInput(newValue);
      setMemberIdInput("");
      scanBuffer.current = newValue;
      lastKeyTime.current = currentTime;
      return;
    }

    // RFID scanners type very fast (< 50ms between characters)
    // If time gap is large (> 500ms), this is likely a new scan - clear previous input
    if (timeDiff > 500 && scanBuffer.current.length > 0 && newValue.length <= scanBuffer.current.length + 1) {
      // New scan starting, but old data might be mixed in
      // Only keep the new character if it looks like a fresh start
      if (newValue.length === 1) {
        scanBuffer.current = newValue;
        setRfidInput(newValue);
      } else {
        scanBuffer.current = newValue;
        setRfidInput(newValue);
      }
    } else {
      scanBuffer.current = newValue;
      setRfidInput(newValue);
    }

    lastKeyTime.current = currentTime;
  }, [result]);

  const handleMemberIdChange = useCallback((e) => {
    if (result) {
      setResult(null);
    }
    setMemberIdInput(e.target.value);
  }, [result]);

  const fetchDisciplines = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${config.apiBaseUrl}/get_disciplines`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const disciplineData = response.data.disciplines || [];
      setDisciplines(disciplineData);
    } catch (error) {
      console.error("Failed to fetch disciplines:", error);
    }
  };

  const handleDisciplineSelect = () => {
    if (!selectedDiscipline) {
      setDisciplineError("Please select a discipline first!");
      return;
    }
    const discipline = disciplines.find(
      (d) => d.discipline_id === selectedDiscipline
    );
    setSelectedDisciplineName(discipline?.discipline_name || "");
    setDisciplineError("");
    setStep("attendance");
  };

  const handleSubmitAttendance = async (
    mode = inputMode,
    scannedMemberId = null,
    selectedQrAction = qrAction
  ) => {
    const rfid = rfidInput.trim();
    const memberId = (scannedMemberId || memberIdInput).trim();

    if (mode === "rfid" && !rfid) {
      setResult({
        type: "error",
        message: "Please enter an RFID!",
        member: null,
      });
      return;
    }

    // Minimum RFID length check to prevent partial reads
    // Most RFID cards have 8-10 digit codes
    const MIN_RFID_LENGTH = 6;
    if (mode === "rfid" && rfid.length < MIN_RFID_LENGTH) {
      setResult({
        type: "error",
        message: `RFID too short (${rfid.length} chars). Please scan again.`,
        member: null,
      });
      setRfidInput("");
      scanBuffer.current = "";
      return;
    }

    if ((mode === "memberId" || mode === "qr") && !memberId) {
      setResult({
        type: "error",
        message: "Please enter a Member ID!",
        member: null,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      let qrLocation = null;
      if (mode === "qr") {
        try {
          qrLocation = await getQrLocation();
        } catch (locationError) {
          setResult({
            type: "error",
            message: locationError.message,
            member: null,
          });
          return;
        }
      }

      const payload = {
        discipline: selectedDiscipline,
      };

      if (mode === "rfid") {
        payload.rfid = rfid;
      } else {
        // QR payloads contain only membership_id and use the existing member-ID API field.
        payload.member_id = memberId;
        if (mode === "qr") {
          payload.latitude = qrLocation.latitude;
          payload.longitude = qrLocation.longitude;
          payload.accuracy = qrLocation.accuracy;
        }
      }

      const token = getAuthToken();
      const endpoint = mode === "qr"
        ? `${config.apiBaseUrl}/qr_attendance/${selectedQrAction}`
        : `${config.apiBaseUrl}/mark_attendance`;
      const response = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 201 || (mode === "qr" && response.status === 200)) {
        let member = response.data.member || response.data.attendance?.member || {};

        // Check-Out returns attendance data only, so load the existing member profile
        // to keep the kiosk success details consistent with Check-In and legacy flows.
        if (mode === "qr" && Object.keys(member).length === 0) {
          try {
            const profileResponse = await axios.get(
              `${config.apiBaseUrl}/member_profile/${encodeURIComponent(memberId)}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            member = profileResponse.data.member || {};
          } catch (profileError) {
            console.warn("Could not load QR member profile details:", profileError);
          }
        }

        setResult({
          type: "success",
          message: mode === "qr"
            ? `QR ${selectedQrAction === "check_in" ? "Check-In" : "Check-Out"} successful!`
            : "Attendance marked successfully!",
          member: Object.keys(member).length > 0 ? member : { membership_id: memberId },
          attendance: response.data.attendance || null,
        });
      } else if (response.status === 200) {
        setResult({
          type: "warning",
          message: response.data.message || "Already marked attendance",
          member: null,
        });
      }
    } catch (error) {
      const errorData = error.response?.data;
      const errorMessage = errorData?.error || errorData?.message || "Connection failed. Check server.";

      // Check if this is a discipline mismatch error
      const enrolledDisciplines = errorData?.enrolled_disciplines || [];
      const memberName = errorData?.member_name || null;

      setResult({
        type: "error",
        message: errorMessage,
        member: null,
        enrolledDisciplines: enrolledDisciplines,
        memberName: memberName,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNextScan = async () => {
    if (inputMode === "qr") {
      // Wait for the previous camera stream to close before starting the next scan.
      await stopQrScanner();
      setMemberIdInput("");
      setQrScannerError("");
      setResult(null);
      setQrScannerKey((currentKey) => currentKey + 1);
      return;
    }

    stopQrScanner();
    setRfidInput("");
    setMemberIdInput("");
    setResult(null);
    scanBuffer.current = "";
    lastKeyTime.current = 0;
    if (rfidInputRef.current) {
      rfidInputRef.current.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmitAttendance();
    }
  };

  const handleInputModeChange = (mode) => {
    stopQrScanner();
    setInputMode(mode);
    setRfidInput("");
    setMemberIdInput("");
    setResult(null);
    setQrScannerError("");
    scanBuffer.current = "";
    lastKeyTime.current = 0;
    setTimeout(() => {
      if (rfidInputRef.current) {
        rfidInputRef.current.focus();
      }
    }, 100);
  };

  const handleBackToDiscipline = () => {
    stopQrScanner();
    setStep("discipline");
    setSelectedDiscipline("");
    setSelectedDisciplineName("");
    setRfidInput("");
    setMemberIdInput("");
    setInputMode("rfid");
    qrActionRef.current = "check_in";
    setQrAction("check_in");
    setQrScannerError("");
    setResult(null);
  };

  useEffect(() => {
    let isMounted = true;

    const updateProfileImageUrl = async () => {
      const resolvedUrl = await resolveProfileImageUrl(result?.member?.profile_image);
      if (isMounted) {
        setProfileImageUrl(resolvedUrl);
      }
    };

    updateProfileImageUrl();

    return () => {
      isMounted = false;
    };
  }, [result]);

  const getProfileImageUrl = (profileImage) => {
    return null;
  };

  return (
    <Box
      sx={{
        height: "100vh",
        backgroundColor: kioskColors.background,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: kioskColors.headerBg,
          py: 3,
          px: 4,
          display: "flex",
          alignItems: "center",
          gap: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
      >
        <Avatar
          src="/assets/logo.jpg"
          sx={{
            width: 80,
            height: 80,
            border: `3px solid ${kioskColors.accent}`,
          }}
        >
          <FitnessCenterIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Box>
          <Typography
            variant="h3"
            sx={{
              color: kioskColors.textPrimary,
              fontWeight: "bold",
              fontFamily: "'Segoe UI', sans-serif",
            }}
          >
            TEBA Attendance
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: kioskColors.textSecondary,
              fontFamily: "'Segoe UI', sans-serif",
            }}
          >
            Mark Your Presence
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          p: 4,
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        {step === "discipline" ? (
          // Discipline Selection Screen
          <Fade in={step === "discipline"}>
            <Card
              sx={{
                backgroundColor: kioskColors.headerBg,
                borderRadius: 3,
                p: 4,
                minWidth: 450,
                maxWidth: 550,
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              }}
            >
              <CardContent>
                <Box textAlign="center" mb={4}>
                  <FitnessCenterIcon
                    sx={{
                      fontSize: 60,
                      color: kioskColors.accent,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h4"
                    sx={{
                      color: kioskColors.textPrimary,
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    Select Your Discipline
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: kioskColors.textSecondary }}
                  >
                    Choose the discipline you're attending today
                  </Typography>
                </Box>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel
                    sx={{
                      color: kioskColors.textSecondary,
                      "&.Mui-focused": { color: kioskColors.accent },
                    }}
                  >
                    Discipline
                  </InputLabel>
                  <Select
                    value={selectedDiscipline}
                    onChange={(e) => {
                      setSelectedDiscipline(e.target.value);
                      setDisciplineError("");
                    }}
                    label="Discipline"
                    sx={{
                      backgroundColor: kioskColors.cardBg,
                      color: kioskColors.textPrimary,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: kioskColors.cardBg,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: kioskColors.accent,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: kioskColors.accent,
                      },
                      "& .MuiSvgIcon-root": {
                        color: kioskColors.textPrimary,
                      },
                    }}
                  >
                    {disciplines.map((discipline) => (
                      <MenuItem
                        key={discipline.discipline_id}
                        value={discipline.discipline_id}
                      >
                        {discipline.discipline_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {disciplineError && (
                  <Typography
                    sx={{
                      color: kioskColors.error,
                      textAlign: "center",
                      mb: 2,
                    }}
                  >
                    {disciplineError}
                  </Typography>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleDisciplineSelect}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    backgroundColor: kioskColors.accent,
                    color: kioskColors.background,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor: "#00b8d4",
                    },
                  }}
                >
                  Continue
                </Button>
              </CardContent>
            </Card>
          </Fade>
        ) : (
          // Attendance Marking Screen
          <Fade in={step === "attendance"}>
            <Box sx={{ width: "100%", maxWidth: 700 }}>
              {/* Selected Discipline Badge */}
              <Card
                sx={{
                  backgroundColor: kioskColors.cardBg,
                  borderRadius: 2,
                  mb: 3,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: kioskColors.accent, fontWeight: "bold" }}
                  >
                    Selected Discipline: {selectedDisciplineName}
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleBackToDiscipline}
                    sx={{ color: kioskColors.textSecondary }}
                  >
                    Change
                  </Button>
                </CardContent>
              </Card>

              {/* RFID Input Section */}
              <Card
                sx={{
                  backgroundColor: kioskColors.headerBg,
                  borderRadius: 3,
                  p: 4,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                }}
              >
                <CardContent>
                  {/* Input Mode Toggle */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Button
                      variant={inputMode === "rfid" ? "contained" : "outlined"}
                      onClick={() => handleInputModeChange("rfid")}
                      startIcon={<CreditCardIcon />}
                      sx={{
                        backgroundColor: inputMode === "rfid" ? kioskColors.accent : "transparent",
                        color: inputMode === "rfid" ? kioskColors.background : kioskColors.textSecondary,
                        borderColor: kioskColors.accent,
                        px: 3,
                        py: 1,
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: inputMode === "rfid" ? "#00b8d4" : "rgba(0,212,255,0.1)",
                          borderColor: kioskColors.accent,
                        },
                      }}
                    >
                      RFID Card
                    </Button>
                    <Button
                      variant={inputMode === "memberId" ? "contained" : "outlined"}
                      onClick={() => handleInputModeChange("memberId")}
                      startIcon={<BadgeIcon />}
                      sx={{
                        backgroundColor: inputMode === "memberId" ? kioskColors.accent : "transparent",
                        color: inputMode === "memberId" ? kioskColors.background : kioskColors.textSecondary,
                        borderColor: kioskColors.accent,
                        px: 3,
                        py: 1,
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: inputMode === "memberId" ? "#00b8d4" : "rgba(0,212,255,0.1)",
                          borderColor: kioskColors.accent,
                        },
                      }}
                    >
                      Member ID
                    </Button>
                    <Button
                      variant={inputMode === "qr" ? "contained" : "outlined"}
                      onClick={() => handleInputModeChange("qr")}
                      startIcon={<QrCodeIcon />}
                      sx={{
                        backgroundColor: inputMode === "qr" ? kioskColors.accent : "transparent",
                        color: inputMode === "qr" ? kioskColors.background : kioskColors.textSecondary,
                        borderColor: kioskColors.accent,
                        px: 3,
                        py: 1,
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: inputMode === "qr" ? "#00b8d4" : "rgba(0,212,255,0.1)",
                          borderColor: kioskColors.accent,
                        },
                      }}
                    >
                      QR Code
                    </Button>
                  </Box>

                  {inputMode === "qr" && (
                    <Box sx={{ mb: 3 }}>
                      {/* QR action is explicit because the same member QR supports both states. */}
                      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}>
                        <Button
                            variant={qrAction === "check_in" ? "contained" : "outlined"}
                            onClick={() => {
                              qrActionRef.current = "check_in";
                              setQrAction("check_in");
                            }}
                          disabled={loading}
                          sx={{
                            backgroundColor: qrAction === "check_in" ? kioskColors.success : "transparent",
                            color: qrAction === "check_in" ? "white" : kioskColors.textSecondary,
                            borderColor: kioskColors.success,
                            fontWeight: "bold",
                          }}
                        >
                          Check-In
                        </Button>
                        <Button
                          variant={qrAction === "check_out" ? "contained" : "outlined"}
                          onClick={() => {
                            qrActionRef.current = "check_out";
                            setQrAction("check_out");
                          }}
                          disabled={loading}
                          sx={{
                            backgroundColor: qrAction === "check_out" ? kioskColors.warning : "transparent",
                            color: qrAction === "check_out" ? "white" : kioskColors.textSecondary,
                            borderColor: kioskColors.warning,
                            fontWeight: "bold",
                          }}
                        >
                          Check-Out
                        </Button>
                      </Box>

                      <Typography
                        variant="h5"
                        sx={{ color: kioskColors.textPrimary, textAlign: "center", mb: 2 }}
                      >
                        Scan the member QR code
                      </Typography>
                      <Box
                        id="attendance-qr-reader"
                        sx={{
                          maxWidth: 360,
                          minHeight: 280,
                          mx: "auto",
                          p: 1,
                          backgroundColor: "#ffffff",
                          borderRadius: 2,
                          overflow: "hidden",
                          "& video": { width: "100%", borderRadius: 1 },
                        }}
                      />
                      {qrScannerError && (
                        <Typography sx={{ color: kioskColors.error, textAlign: "center", mt: 2 }}>
                          {qrScannerError}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Typography
                    variant="h5"
                    sx={{
                      color: kioskColors.textPrimary,
                      textAlign: "center",
                      mb: 3,
                    }}
                  >
                    {inputMode === "rfid"
                      ? "Scan or enter RFID and press Enter"
                      : inputMode === "memberId"
                        ? "Enter Member ID and press Enter"
                        : "Use the camera to scan a TEBA member QR code"}
                  </Typography>

                  {/* Input Area */}
                  {inputMode !== "qr" && <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 3,
                      backgroundColor: kioskColors.cardBg,
                      p: 2,
                      borderRadius: 2,
                    }}
                  >
                    <TextField
                      inputRef={rfidInputRef}
                      fullWidth
                      variant="outlined"
                      placeholder={inputMode === "rfid" ? "Scan or enter RFID..." : "Enter Member ID..."}
                      value={inputMode === "rfid" ? rfidInput : memberIdInput}
                      onChange={inputMode === "rfid" ? handleRfidChange : handleMemberIdChange}
                      onKeyPress={handleKeyPress}
                      disabled={loading}
                      autoFocus
                      autoComplete="off"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: kioskColors.background,
                          color: kioskColors.textPrimary,
                          fontSize: "1.2rem",
                          "& fieldset": {
                            borderColor: "transparent",
                          },
                          "&:hover fieldset": {
                            borderColor: kioskColors.accent,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: kioskColors.accent,
                            borderWidth: 2,
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: kioskColors.textPrimary,
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSubmitAttendance}
                      disabled={loading}
                      sx={{
                        backgroundColor: kioskColors.success,
                        color: "white",
                        px: 4,
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: "#27ae60",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: kioskColors.textMuted,
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </Box>}

                  {/* Loading State */}
                  {loading && (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 4,
                      }}
                    >
                      <CircularProgress
                        sx={{ color: kioskColors.accent }}
                        size={50}
                      />
                      <Typography
                        variant="h6"
                        sx={{ color: kioskColors.accent, mt: 2 }}
                      >
                        Processing...
                      </Typography>
                    </Box>
                  )}

                  {/* Result Display */}
                  {result && !loading && (
                    <Fade in={!!result}>
                      <Box>
                        {result.type === "success" && result.member ? (
                          // Success with Member Info
                          <Card
                            sx={{
                              backgroundColor: kioskColors.cardBg,
                              borderRadius: 3,
                              mt: 2,
                            }}
                          >
                            <CardContent>
                              {/* Success Header */}
                              <Box
                                sx={{
                                  textAlign: "center",
                                  mb: 3,
                                }}
                              >
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="SUCCESS"
                                  sx={{
                                    backgroundColor: kioskColors.success,
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                    py: 2.5,
                                    px: 2,
                                  }}
                                />
                              </Box>

                              {/* Profile Photo */}
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  mb: 3,
                                }}
                              >
                                <Avatar
                                  src={
                                    profileImageUrl ||
                                    getProfileImageUrl(result.member.profile_image)
                                  }
                                  sx={{
                                    width: 150,
                                    height: 150,
                                    border: `4px solid ${kioskColors.accent}`,
                                    fontSize: "3rem",
                                  }}
                                >
                                  {result.member.first_name?.[0]}
                                  {result.member.last_name?.[0]}
                                </Avatar>
                              </Box>

                              {/* Member Name */}
                              <Typography
                                variant="h3"
                                sx={{
                                  color: kioskColors.textPrimary,
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  mb: 3,
                                }}
                              >
                                {result.member.first_name}{" "}
                                {result.member.last_name}
                              </Typography>

                              {/* Member Details */}
                              <Box sx={{ textAlign: "center" }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: kioskColors.accent,
                                    mb: 1,
                                  }}
                                >
                                  ID: {result.member.membership_id || "N/A"}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: kioskColors.textPrimary,
                                    mb: 0.5,
                                  }}
                                >
                                  RFID: {result.member.rfid || "N/A"}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: kioskColors.textPrimary,
                                    mb: 0.5,
                                  }}
                                >
                                  NIC: {result.member.nic || "N/A"}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: kioskColors.textPrimary,
                                    mb: 2,
                                  }}
                                >
                                  Phone: {result.member.mobile_phone || "N/A"}
                                </Typography>

                                <Divider
                                  sx={{
                                    backgroundColor: kioskColors.background,
                                    my: 2,
                                  }}
                                />

                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: kioskColors.textPrimary,
                                    mb: 0.5,
                                  }}
                                >
                                  Branch: {result.member.branch_name || "N/A"}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: kioskColors.textPrimary,
                                    mb: 0.5,
                                  }}
                                >
                                  Discipline: {selectedDisciplineName}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: kioskColors.textPrimary,
                                  }}
                                >
                                  Subscription:{" "}
                                  {result.member.subscription_name || "N/A"}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        ) : (
                          // Warning or Error Message
                          <Box
                            sx={{
                              textAlign: "center",
                              py: 4,
                            }}
                          >
                            {result.type === "warning" ? (
                              <WarningIcon
                                sx={{
                                  fontSize: 60,
                                  color: kioskColors.warning,
                                  mb: 2,
                                }}
                              />
                            ) : (
                              <ErrorIcon
                                sx={{
                                  fontSize: 60,
                                  color: kioskColors.error,
                                  mb: 2,
                                }}
                              />
                            )}
                            {result.memberName && (
                              <Typography
                                variant="h4"
                                sx={{
                                  color: kioskColors.textPrimary,
                                  fontWeight: "bold",
                                  mb: 2,
                                }}
                              >
                                {result.memberName}
                              </Typography>
                            )}
                            <Typography
                              variant="h5"
                              sx={{
                                color:
                                  result.type === "warning"
                                    ? kioskColors.warning
                                    : kioskColors.error,
                                fontWeight: "bold",
                              }}
                            >
                              {result.message}
                            </Typography>
                            {result.enrolledDisciplines && result.enrolledDisciplines.length > 0 && (
                              <Box sx={{ mt: 3 }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: kioskColors.textSecondary,
                                    mb: 1,
                                  }}
                                >
                                  Enrolled disciplines:
                                </Typography>
                                <Box sx={{ display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
                                  {result.enrolledDisciplines.map((disc, index) => (
                                    <Chip
                                      key={index}
                                      label={disc}
                                      sx={{
                                        backgroundColor: kioskColors.accent,
                                        color: kioskColors.background,
                                        fontWeight: "bold",
                                        fontSize: "1rem",
                                        py: 2,
                                      }}
                                    />
                                  ))}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: kioskColors.textMuted,
                                    mt: 2,
                                  }}
                                >
                                  Please select the correct discipline and try again
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Next Button */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 3,
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={handleNextScan}
                            startIcon={<RefreshIcon />}
                            sx={{
                              backgroundColor: kioskColors.accent,
                              color: kioskColors.background,
                              py: 1.5,
                              px: 5,
                              fontSize: "1.1rem",
                              fontWeight: "bold",
                              "&:hover": {
                                backgroundColor: "#00b8d4",
                              },
                            }}
                          >
                            Next
                          </Button>
                        </Box>
                      </Box>
                    </Fade>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: kioskColors.headerBg,
          py: 2,
          textAlign: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: kioskColors.textMuted }}
        >
          © 2025 Total Extreme Budo Academy
        </Typography>
      </Box>
    </Box>
  );
};

export default AttendanceKiosk;

